import React from 'react';
import moment from 'moment-timezone';
import { DatePicker } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { formatReminderCountdown } from '../../data/workOrderStarLabels';
import useReminderNow from '../../hooks/useReminderNow';

interface Props {
	value: moment.Moment | null;
	onChange: (value: moment.Moment | null) => void;
	// Untrack draft: the reminder is shown struck through and can't be edited.
	struck?: boolean;
	defaultOpen?: boolean;
}

export const REMINDER_MINUTE_STEP = 15;

const range = (count: number): number[] => Array.from({ length: count }, (_, index) => index);

// Only times after now can be picked; the backend rejects anything else with a 400.
const disabledDate = (current: moment.Moment) =>
	!!current && current.isBefore(moment().startOf('day'));

const disabledTime = (current: moment.Moment | null) => {
	const now = moment();
	if (!current || !current.isSame(now, 'day')) return {};
	return {
		disabledHours: () => range(now.hour()),
		disabledMinutes: (selectedHour: number) =>
			selectedHour === now.hour() ? range(60).filter((minute) => minute <= now.minute()) : [],
	};
};

const ReminderDateTimeInput: React.FC<Props> = ({
	value,
	onChange,
	struck = false,
	defaultOpen = false,
}) => {
	const now = useReminderNow(value);
	const countdown =
		value && value.isAfter(now) && !struck ? `In ${formatReminderCountdown(value, now)}` : null;

	return (
		<div className="relative min-w-0 flex-1">
			<span className="pointer-events-none absolute inset-y-0 left-3 z-[1] flex items-center">
				<BellOutlined className="text-character-title-85" />
			</span>
			<DatePicker
				value={value}
				onChange={(nextValue) => onChange(nextValue ? nextValue.seconds(0).milliseconds(0) : null)}
				showTime={{ format: 'h:mm a', use12Hours: true, minuteStep: REMINDER_MINUTE_STEP }}
				format="MMMM D, YYYY h:mm a"
				placeholder="Select date and time"
				disabledDate={disabledDate}
				disabledTime={disabledTime}
				showNow={false}
				allowClear={false}
				defaultOpen={defaultOpen}
				disabled={struck}
				suffixIcon={
					countdown ? (
						<span className="footnoteregular whitespace-nowrap text-character-secondary">
							{countdown}
						</span>
					) : (
						<span />
					)
				}
				className={`w-full !pl-8 ${struck ? '[&_input]:line-through' : ''}`}
			/>
		</div>
	);
};

export default ReminderDateTimeInput;
